import { forwardRef, useRef } from "react"
import styled from "@emotion/styled"
import { useTransform, useMotionTemplate, transform, useMotionValue, useInView, useReducedMotion } from "framer-motion"
import useSSR from "hooks/useSSR"

import { useEnv } from "contexts/Env"
import { useConsole } from "contexts/Console"
import { useViewport } from "contexts/Viewport"
import getMediaQuery, { media } from "css/breakpoints"

import { useValue } from "./context"
import Sequence from "./Sequence"
import Image from "components/media/ImageCLD"
import { Bg, ImgBlock, Block, SubContainer, Hgroup, Paragraph } from "./commons"

const ImgContainer = styled.div`
  position: relative;
  grid-column: doc;
  display: flex;

  transform: translate3d(0, 0, 0);

  ${getMediaQuery("m")} {
    grid-column: col 7 / doc-end;
    grid-row: 1;

    .img-first & {
      grid-column: doc-start / span 7;
    }
    [dir="rtl"] .img-first & {
      grid-column: col 7 / span 7;
    }
    [dir="rtl"] & {
      grid-column: doc-start / span 7;
    }
  }

  & canvas {
    transform: translate3d(0, 0, 0);
  }
`

const SequenceBlock = forwardRef(({ className, index, image_portrait_cl, image_landscape_cl, sequence, ...props }, ref) => {
  const console = useConsole()

  const env = useEnv()

  // const prefersReducedMotion = useReducedMotion()
  const prefersReducedMotion = process.browser && document.documentElement.classList.contains("prefers-reduced-motion")

  const { globalProg, inputMap } = useValue()

  const anim_range_start = sequence[0].anim_range_start
  const anim_range_end = sequence[sequence.length - 1].anim_range_end

  const { height, bp } = useViewport()

  const isInView = useInView(ref, { once: true, margin: "100% 0% 50% 0%" })

  const prog = useMotionValue(0)

  const insetVal = useTransform([globalProg, inputMap, height, bp], ([p, inp, h, b]) => {
    const arr = inp.split(",").map(str => Number(str))
    prog.set(transform(p, [arr[index], arr[index + 2] - h], [0, 1]))
    return arr[index] - p
  })

  const inset = useMotionTemplate`inset(${insetVal}px 0 0 0)`

  const [image_portrait, image_landscape] = [image_portrait_cl, image_landscape_cl].map(image => JSON.parse(image)[0])

  const image = {
    sources: [
      {
        public_id: image_portrait.src,
        width: "780",
        height: "844",
        policy: image_portrait.policy ?? "t_v7",
        metadata: { v7_type: ["portrait"] },
        media: media.portrait,
      },
      {
        public_id: image_landscape.src,
        width: "1440",
        height: "1440",
        policy: image_landscape.policy ?? "t_v7-model-feature-material-landscape",
        media: media.landscape,
      },
    ],
  }

  return (
    <ImgBlock className={className} style={{ zIndex: index + 2, marginBlockStart: "-300vh", clipPath: inset }}>
      <ImgContainer>
        {isInView && !prefersReducedMotion ? (
          <Sequence anim_range_start={anim_range_start} anim_range_end={anim_range_end} image={image} index={index} prog={prog} />
        ) : (
          <Image {...image} />
        )}
      </ImgContainer>
    </ImgBlock>
  )
})

function FeatureErsive({ index, topic_key, title, subtitle, text_part_1, text_part_2, image_portrait_cl, image_landscape_cl, order, mode, sequence }, ref) {
  const console = useConsole()
  const ssr = useSSR()

  const isbefore = index - 2 < order.indexOf(topic_key === "bracelet" ? "material" : "bracelet")

  const bgRef = useRef()

  const { height, bp } = useViewport()

  const { globalProg, inputMap } = useValue()

  const mask = useMotionValue("none")

  const opacity = useTransform([globalProg, inputMap, height, bp], ([p, inp, h, b]) => {
    /**  guarantee same start up value as SSR */
    if (ssr) {
      mask.set("none")
      return 1
    } // TODO: what are the good starting values ?

    const [s, e] = b === "s" ? [0.35, 0.2] : [0.55, 0.35]
    const arr = inp.split(",").map(str => Number(str))
    const input = [arr[index] - h * s, arr[index] - h * e, arr[index + 1] - h * s, arr[index + 1] - h * e]
    const diff = arr[index + 1] - arr[index]
    const prog = arr[index] - p
    mask.set(b === "s" ? `linear-gradient(black ${diff - prog - diff * 0.7}px, transparent ${diff - prog - diff * 0.55}px)` : "none")
    return transform(p, input, [0, 1, 1, 0])
  })

  const gradient = useMotionTemplate`${mask}`

  const className = `sive${!!mode ? " img-first" : ""}`

  return (
    <>
      <Bg ref={bgRef} style={{ zIndex: index }} />
      {isbefore ? (
        <SequenceBlock
          ref={ref}
          className={className}
          index={index}
          image_portrait_cl={image_portrait_cl}
          image_landscape_cl={image_landscape_cl}
          sequence={sequence}
        />
      ) : null}
      <Block ref={ref} className={className} style={{ maskImage: gradient, WebkitMaskImage: gradient, zIndex: index }}>
        <SubContainer style={{ opacity }}>
          {title || subtitle ? (
            <Hgroup>
              {title ? <h2 dangerouslySetInnerHTML={{ __html: title }} /> : null}
              {subtitle ? <h3 dangerouslySetInnerHTML={{ __html: subtitle }} /> : null}
            </Hgroup>
          ) : null}
          {text_part_1 ? <Paragraph dangerouslySetInnerHTML={{ __html: text_part_1 }} /> : null}
          {text_part_2 ? <Paragraph dangerouslySetInnerHTML={{ __html: text_part_2 }} /> : null}
        </SubContainer>
      </Block>
    </>
  )
}

export default forwardRef(FeatureErsive)
